import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { baseAPI } from '../../config';
import { toast } from 'react-toastify';
import moment from 'moment';
import CreateAssignmentModal from './components/CreateAssignmentModal';
import EditAssignmentModal from './components/EditAssignmentModal';
import {
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_STATUS_LABELS,
  ASSIGNMENT_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
} from '@claimup/shared/constants/assignments';
import { assigneeName } from '../../utils/assignmentLabels';

const AssignmentsSection = ({ expedientId, fullHeight = true }) => {
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [expandedAssignments, setExpandedAssignments] = useState(new Set());
  const [updatingStatus, setUpdatingStatus] = useState(new Set());

  const heightContent = fullHeight ? 'max-h-none' : 'max-h-96';

  const toggleAssignmentDetails = assignmentId => {
    setExpandedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const fetchAssignments = useCallback(async () => {
    if (!expedientId) {
      setAssignments([]);
      return;
    }

    try {
      setLoadingAssignments(true);
      const expedientIdStr = typeof expedientId === 'object' ? expedientId.toString() : expedientId;
      // 🔴 Abierta = `pending` O `in_progress`. Antes solo pedía `pending`.
      //
      // Una tarea pasa a `in_progress` en el momento en que el agente la coge, y
      // se queda ahí mientras espera el visto bueno de una persona — o sea,
      // durante toda la parte en la que alguien puede querer mirarla. Pidiendo
      // solo `pending`, la ficha decía «No hay assignments para este expediente»
      // exactamente cuando el trabajo estaba en marcha.
      //
      // El 14/09/2026 Edgar miró dos expedientes que estaban en la cola de
      // revisión (AHMAD NAVEED y Jorge Eduardo Bailón), vio ese cartel y
      // concluyó —con toda la lógica— que el despachador estaba dejando entrar
      // expedientes sin assignment. Los dos tenían el suyo, `in_progress` y
      // asignado al agente. La regla se cumplía; la pantalla no la enseñaba.
      const statusFilter = showAll ? '' : '&status=pending&status=in_progress';
      const response = await axios.get(`${baseAPI}/assignments?expedient_id=${expedientIdStr}${statusFilter}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setAssignments(response.data?.assignments || []);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Error al cargar assignments');
    } finally {
      setLoadingAssignments(false);
    }
  }, [expedientId, showAll]);

  // Fetch assignments for the current expedient
  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleAssignmentCreated = () => {
    // Refresh assignments list after creation
    fetchAssignments();
  };

  const handleAssignmentUpdated = () => {
    // Refresh assignments list after update
    fetchAssignments();
  };

  const handleStatusChange = async (assignmentId, newStatus) => {
    // Prevent changes if already updating
    if (updatingStatus.has(assignmentId)) {
      return;
    }

    // Set loading state for this specific assignment
    setUpdatingStatus(prev => new Set(prev).add(assignmentId));

    try {
      await axios.put(
        `${baseAPI}/assignments/${assignmentId}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Update local state
      if (showAll) {
        // If showing all, just update the status
        setAssignments(prev =>
          prev.map(assignment => (assignment._id === assignmentId ? { ...assignment, status: newStatus } : assignment))
        );
      } else {
        // If showing only pending, remove assignment if status is no longer pending
        if (newStatus === 'pending' || newStatus === 'in_progress') {
          setAssignments(prev =>
            prev.map(assignment =>
              assignment._id === assignmentId ? { ...assignment, status: newStatus } : assignment
            )
          );
        } else {
          // Remove assignment from list if it's no longer pending
          setAssignments(prev => prev.filter(assignment => assignment._id !== assignmentId));
        }
      }
    } catch (error) {
      console.error('Error updating assignment status:', error);
      toast.error('Error al actualizar el estado');
    } finally {
      // Remove loading state for this assignment
      setUpdatingStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(assignmentId);
        return newSet;
      });
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = priority => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 font-bold';
      case 'high':
        return 'text-orange-600 font-semibold';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  if (!expedientId) {
    return null;
  }

  return (
    <div className='w-full bg-white border border-slate-200 rounded-lg shadow-lg'>
      {/* Header */}
      <div className='px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between rounded-t-lg'>
        <h3 className='text-sm font-semibold text-slate-900'>Gestiones</h3>
        <div className='flex items-center gap-2'>
          {!showAll && (
            <button
              onClick={() => setShowAll(true)}
              className='text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors'
            >
              Ver todos
            </button>
          )}
          {showAll && (
            <button
              onClick={() => setShowAll(false)}
              className='text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors'
            >
              Ver solo pendientes
            </button>
          )}
          {/* Create Assignment Modal */}
          <CreateAssignmentModal expedientId={expedientId} onAssignmentCreated={handleAssignmentCreated} />
        </div>
      </div>

      {/* Content */}
      <div className='p-2'>
        {loadingAssignments ? (
          <div className='text-center py-4'>
            <div className='inline-flex items-center justify-center'>
              <svg className='animate-spin w-5 h-5 fill-current text-slate-400' viewBox='0 0 24 24'>
                <path d='M12 22c5.523 0 10-4.477 10-10h-2c0 4.418-3.582 8-8 8s-8-3.582-8-8c0-4.418 3.582-8 8-8V2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10z' />
              </svg>
              <span className='ml-2 text-sm text-slate-500'>Cargando...</span>
            </div>
          </div>
        ) : assignments.length === 0 ? (
          <p className='text-sm text-slate-500 text-center py-4'>No hay assignments para este expediente</p>
        ) : (
          <div className={`space-y-1 ${heightContent} overflow-y-auto`}>
            {assignments.map(assignment => {
              const isExpanded = expandedAssignments.has(assignment._id);
              return (
                <div
                  key={assignment._id}
                  className='p-4 rounded-lg border border-slate-200 hover:shadow-sm transition-shadow'
                >
                  <div className='flex items-start justify-between gap-4'>
                    <div className='flex-1 min-w-0 space-y-2'>
                      {/* Header: Date, Priority, Task Type */}
                      <div className='flex items-center flex-wrap gap-2'>
                        <span className='text-xs text-slate-600 font-medium'>
                          {moment(assignment.date).format('DD/MM/YYYY - HH:mm')} h
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-lg ${getPriorityColor(assignment.priority)} bg-slate-100`}
                        >
                          {ASSIGNMENT_PRIORITY_LABELS[assignment.priority] || assignment.priority}
                        </span>
                        {assignment.task_type && (
                          <span className='text-xs text-slate-600 px-2 py-0.5 rounded-lg bg-blue-50'>
                            {TASK_TYPE_LABELS[assignment.task_type] || assignment.task_type}
                          </span>
                        )}
                      </div>

                      {/* Description with expand/collapse icon */}
                      <button
                        onClick={() => toggleAssignmentDetails(assignment._id)}
                        className='flex items-start gap-2 w-full text-left hover:opacity-80 transition-opacity'
                      >
                        <span className='text-slate-600 font-bold text-lg leading-none mt-0 flex-shrink-0'>
                          {isExpanded ? '−' : '+'}
                        </span>
                        <p className='text-sm text-slate-900 font-medium flex-1'>{assignment.description}</p>
                      </button>

                      {/* Assigned To */}
                      {assignment.assigned_to && (
                        <div className='text-xs text-slate-600 ml-6'>
                          <span className='font-semibold text-slate-700'>Asignado a:</span>{' '}
                          <span>{assigneeName(assignment.assigned_to)}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Select */}
                    <div className='flex-shrink-0 flex flex-col items-end gap-1'>
                      <div className='flex items-center gap-2'>
                        {updatingStatus.has(assignment._id) && (
                          <svg className='animate-spin w-4 h-4 fill-current text-slate-400' viewBox='0 0 24 24'>
                            <path d='M12 22c5.523 0 10-4.477 10-10h-2c0 4.418-3.582 8-8 8s-8-3.582-8-8c0-4.418 3.582-8 8-8V2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10z' />
                          </svg>
                        )}
                        <select
                          value={assignment.status}
                          onChange={e => handleStatusChange(assignment._id, e.target.value)}
                          disabled={updatingStatus.has(assignment._id)}
                          className={`text-xs rounded-lg px-2 pr-5 py-0.5 border-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors ${
                            updatingStatus.has(assignment._id) ? 'opacity-50 cursor-not-allowed' : ''
                          } ${getStatusColor(assignment.status)}`}
                          style={{
                            borderColor:
                              assignment.status === 'pending'
                                ? '#fbbf24'
                                : assignment.status === 'in_progress'
                                  ? '#60a5fa'
                                  : assignment.status === 'completed'
                                    ? '#34d399'
                                    : assignment.status === 'cancelled'
                                      ? '#f87171'
                                      : '#e5e7eb',
                          }}
                        >
                          {ASSIGNMENT_STATUSES.map(status => (
                            <option key={status} value={status} className='pr-4'>
                              {ASSIGNMENT_STATUS_LABELS[status]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className='space-y-2 pt-2 border-t border-slate-200'>
                      {/* Notes */}
                      {assignment.notes && (
                        <div>
                          <p className='text-xs font-semibold text-slate-700 mb-1'>Notas:</p>
                          <p className='text-xs text-slate-600 italic bg-white p-2 rounded-lg border border-slate-200'>
                            {assignment.notes}
                          </p>
                        </div>
                      )}

                      {/* Additional Details Grid */}
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600 pt-1'>
                        {/* Creator */}
                        {assignment.creator_id && (
                          <div>
                            <span className='font-semibold text-slate-700'>Creado por:</span>{' '}
                            <span>
                              {assignment.creator_id.username} {assignment.creator_id.userlastname}
                            </span>
                          </div>
                        )}

                        {/* Created At */}
                        {assignment.createdAt && (
                          <div className='flex items-center gap-2'>
                            <span className='font-semibold text-slate-700'>Creado:</span>
                            <span>{moment(assignment.createdAt).format('DD/MM/YYYY - HH:mm')} h</span>
                            <div className='ml-auto' onClick={e => e.stopPropagation()}>
                              <EditAssignmentModal
                                assignment={assignment}
                                onAssignmentUpdated={handleAssignmentUpdated}
                              />
                            </div>
                          </div>
                        )}

                        {/* Completed At */}
                        {assignment.completed_at && (
                          <div>
                            <span className='font-semibold text-slate-700'>Completado:</span>{' '}
                            <span>{moment(assignment.completed_at).format('DD/MM/YYYY - HH:mm')} h</span>
                          </div>
                        )}

                        {/* Completed By */}
                        {assignment.completed_by && (
                          <div>
                            <span className='font-semibold text-slate-700'>Completado por:</span>{' '}
                            <span>
                              {assignment.completed_by.username} {assignment.completed_by.userlastname}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentsSection;
